import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";

// Default shrimp logo for Bubba Gump (served from local public folder)
const SHRIMP_LOGO_URL = "/images/shrimp-logo.png";

// Scan-safety rules (validated with OpenCV against all employee URLs):
//  - integer pixels per module (no anti-aliased half-modules)
//  - 4-module quiet zone (ISO standard)
//  - logo ≤ 25% of QR width, outline ≈ 0.6 module
const QUIET_ZONE = 4;
const LOGO_RATIO = 0.25;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const drawFallbackLogo = (ctx, cx, cy, logoSize, bgColor) => {
  ctx.fillStyle = bgColor;
  ctx.beginPath();
  ctx.arc(cx, cy, logoSize / 2 + 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#C41E3A";
  ctx.beginPath();
  ctx.arc(cx, cy, logoSize / 2 - 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#FFFFFF";
  ctx.font = `bold ${logoSize * 0.45}px Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("BG", cx, cy + 2);
};

/**
 * Draw a scan-safe styled QR onto `canvas`. The QR is rendered at an integer
 * module scale so the final image is `size` px (or the closest multiple).
 */
export async function drawStyledQR(canvas, url, options = {}) {
  const {
    size = 400,
    logoUrl = SHRIMP_LOGO_URL,
    qrColor = "#000000",
    bgColor = "#FFFFFF",
    frameColor = "#1a1a2e",
    frameWidth = 0,
    frameRadius = 0,
    showFrame = false,
  } = options;

  const modelSize = QRCode.create(url, { errorCorrectionLevel: "H" }).modules.size;
  const scale = Math.max(2, Math.round(size / (modelSize + QUIET_ZONE * 2)));
  const qrPx = scale * (modelSize + QUIET_ZONE * 2);
  const totalSize = showFrame ? qrPx + frameWidth * 2 : qrPx;
  const qrX = showFrame ? frameWidth : 0;

  canvas.width = totalSize;
  canvas.height = totalSize;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, totalSize, totalSize);

  if (showFrame) {
    ctx.fillStyle = frameColor;
    ctx.beginPath();
    ctx.roundRect(0, 0, totalSize, totalSize, frameRadius);
    ctx.fill();
  }

  const qrCanvas = document.createElement("canvas");
  await QRCode.toCanvas(qrCanvas, url, {
    scale,
    margin: QUIET_ZONE,
    errorCorrectionLevel: "H",
    color: { dark: qrColor, light: bgColor },
  });
  ctx.fillStyle = bgColor;
  ctx.fillRect(qrX, qrX, qrPx, qrPx);
  ctx.drawImage(qrCanvas, qrX, qrX);

  const cx = totalSize / 2;
  const cy = totalSize / 2;
  const logoSize = Math.round(qrPx * LOGO_RATIO);
  const logoX = cx - logoSize / 2;
  const logoY = cy - logoSize / 2;
  const outline = Math.max(3, Math.round(scale * 0.6));

  try {
    if (!logoUrl) throw new Error("no logo");
    const img = await loadImage(logoUrl);
    // Form-fitting white outline: stamp the logo's silhouette at every offset.
    const mask = document.createElement("canvas");
    mask.width = totalSize;
    mask.height = totalSize;
    const mctx = mask.getContext("2d");
    for (let x = -outline; x <= outline; x++) {
      for (let y = -outline; y <= outline; y++) {
        mctx.drawImage(img, logoX + x, logoY + y, logoSize, logoSize);
      }
    }
    mctx.globalCompositeOperation = "source-in";
    mctx.fillStyle = bgColor;
    mctx.fillRect(0, 0, totalSize, totalSize);
    ctx.drawImage(mask, 0, 0);
    ctx.drawImage(img, logoX, logoY, logoSize, logoSize);
  } catch {
    drawFallbackLogo(ctx, cx, cy, logoSize, bgColor);
  }

  return { totalSize, modelSize, scale };
}

/**
 * Styled QR Code preview component.
 */
export default function StyledQRCode({
  url,
  size = 280,
  logoUrl = SHRIMP_LOGO_URL,
  qrColor = "#000000",
  bgColor = "#FFFFFF",
  frameColor = "#1a1a2e",
  frameWidth = 0,
  frameRadius = 0,
  showFrame = false,
  className = "",
}) {
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!canvasRef.current || !url) return;
    drawStyledQR(canvasRef.current, url, { size, logoUrl, qrColor, bgColor, frameColor, frameWidth, frameRadius, showFrame })
      .then(() => setError(null))
      .catch((err) => {
        console.error("QR generation error:", err);
        setError("Failed to generate QR code");
      });
  }, [url, size, logoUrl, qrColor, bgColor, frameColor, frameWidth, frameRadius, showFrame]);

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-slate-800 rounded-lg ${className}`} style={{ width: size, height: size }}>
        <span className="text-red-400 text-sm">{error}</span>
      </div>
    );
  }

  return <canvas ref={canvasRef} className={className} data-testid="styled-qr-canvas" style={{ maxWidth: "100%", height: "auto" }} />;
}

/**
 * Generate a styled QR code as a PNG data URL for downloading.
 */
export async function generateStyledQRDataUrl(url, options = {}) {
  const canvas = document.createElement("canvas");
  await drawStyledQR(canvas, url, { size: 600, ...options });
  return canvas.toDataURL("image/png");
}
